import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Objective } from 'src/app/models/schema';
import { SignOut } from 'src/app/store/auth/auth.actions';
import { SetActiveTeam } from 'src/app/rxjs-store/account/active-team/active-team.actions';
import {
  LoadObjectives,
  LoadObjectivesSuccess,
  LoadObjectivesFailure,
  AddObjectivesBatch,
  AddObjectivesBatchSuccess,
  AddObjectivesBatchFailure,
  UpdateObjective,
  UpdateObjectiveSuccess,
  UpdateObjectiveFailure,
  DeleteObjective,
  DeleteObjectiveSuccess,
  DeleteObjectiveFailure,
  DeleteObjectivesByGoal,
  DeleteObjectivesByGoalSuccess,
  DeleteObjectivesByGoalFailure,
  ResetObjectivesState,
} from './objectives.actions';

/**
 * Objectives State Model
 */
export interface ObjectivesStateModel {
  /** Array of all objectives for the active partner */
  objectives: Objective[];
  
  /** Loading state indicator */
  loading: boolean;
  
  /** Error message if any operation failed */
  error: string | null;
  
  /** Timestamp of last successful sync with Firestore */
  lastSync: number | null;
}

/**
 * Default state
 */
const defaults: ObjectivesStateModel = {
  objectives: [],
  loading: false,
  error: null,
  lastSync: null,
};

/**
 * Objectives State
 * 
 * Manages objectives (goal milestones and targets) for the active partner.
 * Objectives are stored separately from goals for better performance and flexibility.
 */
@State<ObjectivesStateModel>({
  name: 'objectives',
  defaults,
})
@Injectable()
export class ObjectivesState {
  constructor(private store: Store) {}

  /**
   * Select all objectives
   */
  @Selector()
  static objectives(state: ObjectivesStateModel): Objective[] {
    return state.objectives;
  }

  /**
   * Select objectives by goal ID
   */
  @Selector()
  static objectivesByGoal(state: ObjectivesStateModel) {
    return (goalId: string): Objective[] => {
      return state.objectives.filter((o) => o.goalId === goalId);
    };
  }

  /**
   * Select objectives by student ID
   */
  @Selector()
  static objectivesByStudent(state: ObjectivesStateModel) {
    return (studentId: string): Objective[] => {
      return state.objectives.filter((o) => o.studentId === studentId);
    };
  }

  /**
   * Select objectives by skill ID
   */
  @Selector()
  static objectivesBySkill(state: ObjectivesStateModel) {
    return (skillId: string): Objective[] => {
      return state.objectives.filter((o) => o.skillId === skillId);
    };
  }

  /**
   * Select upcoming milestones within specified days
   */
  @Selector()
  static upcomingMilestones(state: ObjectivesStateModel) {
    return (days: number = 30): Objective[] => {
      const now = Date.now();
      const futureDate = now + days * 24 * 60 * 60 * 1000;
      return state.objectives
        .filter((o) => o.targetDate >= now && o.targetDate <= futureDate)
        .sort((a, b) => a.targetDate - b.targetDate);
    };
  }

  /**
   * Select loading state
   */
  @Selector()
  static loading(state: ObjectivesStateModel): boolean {
    return state.loading;
  }

  /**
   * Select error state
   */
  @Selector()
  static error(state: ObjectivesStateModel): string | null {
    return state.error;
  }

  /**
   * Select last sync timestamp
   */
  @Selector()
  static lastSync(state: ObjectivesStateModel): number | null {
    return state.lastSync;
  }

  /**
   * Load objectives
   */
  @Action(LoadObjectives)
  loadObjectives(ctx: StateContext<ObjectivesStateModel>, action: LoadObjectives) {
    ctx.patchState({ loading: true, error: null });
  }

  @Action(LoadObjectivesSuccess)
  loadObjectivesSuccess(ctx: StateContext<ObjectivesStateModel>, action: LoadObjectivesSuccess) {
    const state = ctx.getState();
    const existingObjectives = state.objectives;
    
    // Merge new objectives with existing ones (upsert based on ID)
    const objectivesMap = new Map<string, Objective>();
    existingObjectives.forEach((obj) => objectivesMap.set(obj.id, obj));
    action.objectives.forEach((obj) => objectivesMap.set(obj.id, obj));
    
    ctx.patchState({
      objectives: Array.from(objectivesMap.values()),
      loading: false,
      lastSync: Date.now(),
    });
  }

  @Action(LoadObjectivesFailure)
  loadObjectivesFailure(ctx: StateContext<ObjectivesStateModel>, action: LoadObjectivesFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Add objectives batch
   */
  @Action(AddObjectivesBatch)
  addObjectivesBatch(ctx: StateContext<ObjectivesStateModel>, action: AddObjectivesBatch) {
    ctx.patchState({ loading: true, error: null });
  }

  @Action(AddObjectivesBatchSuccess)
  addObjectivesBatchSuccess(ctx: StateContext<ObjectivesStateModel>, action: AddObjectivesBatchSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      objectives: [...state.objectives, ...action.objectives],
      loading: false,
    });
  }

  @Action(AddObjectivesBatchFailure)
  addObjectivesBatchFailure(ctx: StateContext<ObjectivesStateModel>, action: AddObjectivesBatchFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Update objective
   */
  @Action(UpdateObjective)
  updateObjective(ctx: StateContext<ObjectivesStateModel>, action: UpdateObjective) {
    ctx.patchState({ loading: true, error: null });
  }

  @Action(UpdateObjectiveSuccess)
  updateObjectiveSuccess(ctx: StateContext<ObjectivesStateModel>, action: UpdateObjectiveSuccess) {
    const state = ctx.getState();
    const objectives = state.objectives.map((obj) =>
      obj.id === action.objective.id ? action.objective : obj
    );
    
    ctx.patchState({
      objectives,
      loading: false,
    });
  }

  @Action(UpdateObjectiveFailure)
  updateObjectiveFailure(ctx: StateContext<ObjectivesStateModel>, action: UpdateObjectiveFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Delete objective
   */
  @Action(DeleteObjective)
  deleteObjective(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjective) {
    ctx.patchState({ loading: true, error: null });
  }

  @Action(DeleteObjectiveSuccess)
  deleteObjectiveSuccess(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjectiveSuccess) {
    const state = ctx.getState();
    const objectives = state.objectives.filter((obj) => obj.id !== action.objectiveId);
    
    ctx.patchState({
      objectives,
      loading: false,
    });
  }

  @Action(DeleteObjectiveFailure)
  deleteObjectiveFailure(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjectiveFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Delete objectives by goal
   */
  @Action(DeleteObjectivesByGoal)
  deleteObjectivesByGoal(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjectivesByGoal) {
    ctx.patchState({ loading: true, error: null });
  }

  @Action(DeleteObjectivesByGoalSuccess)
  deleteObjectivesByGoalSuccess(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjectivesByGoalSuccess) {
    const state = ctx.getState();
    const objectives = state.objectives.filter((obj) => obj.goalId !== action.goalId);
    
    ctx.patchState({
      objectives,
      loading: false,
    });
  }

  @Action(DeleteObjectivesByGoalFailure)
  deleteObjectivesByGoalFailure(ctx: StateContext<ObjectivesStateModel>, action: DeleteObjectivesByGoalFailure) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }

  /**
   * Reset state (on sign out or team change)
   */
  @Action(ResetObjectivesState)
  @Action(SignOut)
  @Action(SetActiveTeam)
  resetState(ctx: StateContext<ObjectivesStateModel>) {
    ctx.patchState(defaults);
  }
}
